/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

/**
 * Configuration de lecture d'un utilisateur pour une version de texte.
 *
 * Synchronisée avec LWW (Last Write Wins) basé sur updatedAt.
 * Le client contrôle updatedAt pour garantir la cohérence offline/online.
 */
export interface VersionConfig {
  id: string
  versionId: string
  userId: string

  // ── Voix off ──────────────────────────────────────────────────────────────
  /** Lire les didascalies dans les répliques */
  readStageDirections: boolean
  /** Lire les didascalies hors répliques (de scène) */
  readSceneDirections: boolean
  /** Lire la structure (actes, scènes, titres) */
  readStructure: boolean
  /** Lire la section distribution (présentation) */
  readPresentation: boolean

  // ── Vitesses ──────────────────────────────────────────────────────────────
  /** Vitesse de lecture générale (clampée [0.5, 2.0]) */
  readingSpeed: number
  /** Vitesse des répliques utilisateur en mode italienne (clampée [0.5, 2.0]) */
  userSpeed: number

  // ── Italienne ─────────────────────────────────────────────────────────────
  /** ID du personnage joué par l'utilisateur */
  userCharacterId?: string
  /** Cacher les répliques utilisateur avant lecture */
  hideUserLinesBefore: boolean
  /** Cacher les répliques utilisateur pendant lecture */
  hideUserLinesDuring: boolean
  /** Cacher les répliques utilisateur après lecture */
  hideUserLinesAfter: boolean
  /** Lire les répliques utilisateur avant (voix off) */
  readUserLinesBefore: boolean
  /** Lire les répliques utilisateur après (voix off) */
  readUserLinesAfter: boolean

  // ── Quick Access ──────────────────────────────────────────────────────────
  /** Passer les scènes longues sans le personnage (>10 répliques avant) */
  skipLongScenes: boolean
  /** Abréger les longues répliques (>30s) aux 10 dernières secondes */
  abbreviateLongLines: boolean

  // ── Sync ──────────────────────────────────────────────────────────────────
  syncStatus: 'PENDING' | 'SYNCED'
  /** ISO timestamp contrôlé par le client pour LWW */
  updatedAt: string
  createdAt: string
}

/**
 * Valeurs par défaut pour une nouvelle VersionConfig.
 * Exclut les champs d'identité (id, versionId, userId, createdAt, updatedAt).
 */
export const DEFAULT_VERSION_CONFIG: Omit<
  VersionConfig,
  'id' | 'versionId' | 'userId' | 'createdAt' | 'updatedAt'
> = {
  readStageDirections: true,
  readSceneDirections: true,
  readStructure: true,
  readPresentation: false,
  readingSpeed: 1.0,
  userSpeed: 1.0,
  userCharacterId: undefined,
  hideUserLinesBefore: false,
  hideUserLinesDuring: true,
  hideUserLinesAfter: false,
  readUserLinesBefore: false,
  readUserLinesAfter: false,
  skipLongScenes: false,
  abbreviateLongLines: false,
  syncStatus: 'PENDING',
}

/**
 * Réponse serveur pour une VersionConfig (camelCase, pas de syncStatus).
 */
export interface VersionConfigResponse {
  id: string
  versionId: string
  userId: string
  readStageDirections: boolean
  readSceneDirections: boolean
  readStructure: boolean
  readPresentation: boolean
  readingSpeed: number
  userSpeed: number
  userCharacterId?: string
  hideUserLinesBefore: boolean
  hideUserLinesDuring: boolean
  hideUserLinesAfter: boolean
  readUserLinesBefore: boolean
  readUserLinesAfter: boolean
  skipLongScenes: boolean
  abbreviateLongLines: boolean
  createdAt: string
  updatedAt: string
}
